import { useRouter } from 'next/router';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';

const PreviousUrlContext = createContext<string | undefined>(undefined);

type Props = {
  children: ReactNode;
};

export function usePreviousUrl() {
  return useContext(PreviousUrlContext);
}

/**
 * Use session storage because
 * 1. SSR / static optimization will kill any useState implementation
 * 2. Session storage will be cleared out on every session rather than localStorage
 * which will persist
 */
function updateStorage(url: string) {
  let storage;

  try {
    storage = globalThis?.sessionStorage;
  } catch (e) {
    // If we don't have access to session storage, we can't save the previous url. Bail out.
  }

  if (!storage) return;

  // Set the previous path as the value of the current path.
  const prevPath = storage.getItem('currentIdUrl') || '';
  storage.setItem('prevIdUrl', prevPath);

  // Set the current path value by looking at the browser's location object.
  storage.setItem('currentIdUrl', url);

  return prevPath;
}

export function PreviousUrlProvider(props: Props) {
  const [previousUrl, setPreviousUrl] = useState<string | undefined>();
  const router = useRouter();

  useEffect(() => {
    function handleRouteChange(url: string) {
      const prevUrl = updateStorage(url);
      setPreviousUrl(prevUrl);
    }

    // Subscribe to route changes
    router.events.on('routeChangeStart', handleRouteChange);

    // Unsubscribe when unmounted
    return function () {
      router.events.off('routeChangeStart', handleRouteChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Create provider */
  return (
    <PreviousUrlContext.Provider value={previousUrl}>
      {props.children}
    </PreviousUrlContext.Provider>
  );
}
